import {
  confirmDialog,
  CustomButton,
  CustomCollapseCard,
  CustomImage,
  CustomPagination,
  CustomTable,
  successDialog,
  Text,
  useText,
} from '@src/components';
import cardsRegistered from '@src/assets/icons/icon-cards-registered.svg';
import React, { useCallback, useEffect } from 'react';
import '@src/styles/draftOrder/draftList.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  copyLoadBalance,
  getDraftedLoadBalances,
  loadBalancesUpdate,
} from '@src/store/slice/balanceUploadSlice';
import { moneyFormat } from '@src/utils/utils';
import { useHistory } from 'react-router-dom';
import dayjs from '@src/utils/day';
import { defaultDateFormat } from '@src/utils/keys';

const LoadBalanceDraftList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { draftedLoadBalances, draftedTableProperty } = useSelector(
    (state) => state?.balanceUpload,
  );
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const deleteDraftConfirmation = useText('deleteDraftConfirmation');
  const loadDraftedLoadBalances = useCallback(
    async (pageNumber, pageSize) => {
      dispatch(getDraftedLoadBalances({ pageNumber, pageSize }));
    },
    [dispatch],
  );

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await loadDraftedLoadBalances(pageNumber, pageSize);
    },
    [loadDraftedLoadBalances],
  );

  useEffect(() => {
    (async () => {
      await loadDraftedLoadBalances(1, 10);
    })();
  }, [loadDraftedLoadBalances]);

  const acceptDraft = async (record) => {
    const action = await dispatch(copyLoadBalance({ sourceId: record?.id }));
    if (copyLoadBalance.fulfilled.match(action)) {
      history.push(`/ticket-restaurant/balance-upload?balanceId=${action.payload.data.id}`);
    }
  };

  const deleteDraft = async (record) => {
    confirmDialog({
      title: <Text t="attention" />,
      message: deleteDraftConfirmation,
      okText: <Text t="delete" />,
      cancelText: <Text t="giveUp" />,
      onOk: async () => {
        let data = {
          id: record.id,
          draft: false,
          IsDeletedDraft: true,
        };

        const action = await dispatch(loadBalancesUpdate(data));
        if (loadBalancesUpdate.fulfilled.match(action)) {
          successDialog({
            title: <Text t="successfullySent" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: () => {
              const { currentPage, pageSize } = draftedTableProperty;

              if (currentPage > 1 && draftedLoadBalances.length <= 1) {
                loadDraftedLoadBalances(currentPage - 1, pageSize);
              } else {
                loadDraftedLoadBalances(currentPage, pageSize);
              }
            },
          });
        }
      },
    });
  };

  const columns = [
    {
      title: <Text t="orderNoUpperCase" />,
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 100,
    },
    {
      title: <Text t="orderDate" />,
      dataIndex: 'insertTime',
      key: 'insertTime',
      width: 162.7,
      render: (text) => {
        return text ? dayjs(text).format(defaultDateFormat) : '-';
      },
    },
    {
      title: <Text t="totalCardsUpperCase" />,
      dataIndex: 'totalCount',
      key: 'totalCount',
      align: 'center',
      width: 162.7,
    },
    {
      title: <Text t="orderPrice" />,
      dataIndex: 'totalPaymentPrice',
      key: 'totalPaymentPrice',
      width: 150,
      align: 'right',
      render: (text) => <span>{text ? `${moneyFormat(text, false)}₺` : '0'}</span>,
    },
    {
      title: <Text t="viewOrder" />,
      width: 162.7,
      dataIndex: 'orderViewAction',
      key: 'orderViewAction',
      align: 'center',
      render: (_, record) => {
        return (
          <div className="action-btns">
            <CustomButton
              id="ticketRestaurant-draftOrders-loadBalanceDraftList-detail-btn"
              className="detail-btn"
              onClick={() => acceptDraft(record)}
            >
              <Text t="viewOrder" />
            </CustomButton>
          </div>
        );
      },
    },
    {
      title: <Text t="delete" />,
      dataIndex: 'draftDeleteAction',
      key: 'draftDeleteAction',
      width: 100,
      align: 'center',
      render: (_, record) => {
        return (
          <div className="action-btns">
            <CustomButton
              id="ticketRestaurant-draftOrders-loadBalanceDraftList-delete-btn"
              className="delete-btn"
              onClick={() => deleteDraft(record)}
            >
              <Text t="delete" />
            </CustomButton>
          </div>
        );
      },
    },
  ];

  return (
    <CustomCollapseCard className="draft-list-card" cardTitle={<Text t="balanceLoadingDrafts" />}>
      <div className="number-registered-drafts">
        <CustomImage src={cardsRegistered} />
        <div className="drafts-count-title">
          <Text t="registeredOrderDraft" /> <span>{draftedTableProperty.totalCount}</span>
        </div>
      </div>

      <CustomTable
        pagination={false}
        dataSource={draftedLoadBalances}
        columns={columns}
        rowKey={(record) => `draft-list-load-balance-${record?.id || record?.name}`}
      />

      <div className="draft-list-footer">
        <CustomPagination
          current={draftedTableProperty?.currentPage}
          defaultCurrent={1}
          pageSize={draftedTableProperty?.pageSize}
          total={draftedTableProperty?.totalCount}
          onChange={async (pageNumber, pageSize) => {
            await handleTableChange(pageNumber, pageSize);
          }}
        />
      </div>
    </CustomCollapseCard>
  );
};

export default LoadBalanceDraftList;
